import { ActiveBuff, BuffModifier, EntityEvent } from '../types/buff.types';
import type { BuffDefinition } from '../types/buff.types';
import type { IEntityStats } from '../types/entity.types';
import type { DungeonEntity } from '../entities/DungeonEntity';
import { BUFF_REGISTRY } from '../data/buffs.config';
import { EFFECT_HANDLERS } from '../effect-system/effectHandlers';
import { EffectContext } from '../types/spell.types';
import { checkEffectConditions } from '../effect-system/conditionChecker';
import { DirectionEnum, DungeonEntityType } from '../types/enums';
import { msToTicks } from './timing.utils';
import { emitItemListeners } from './itemListener.utils';
import type { PlayerEntity } from '../entities/PlayerEntity';

/**
 * Folds permanent stat modifiers into `stats` with the universal formula
 * `(base + Σ flats) × (1 + Σ %/100)` per stat — the same as `recomputeStats`,
 * applied once. Used at fresh-run creation for the permanent progression
 * sources (talent tree nodes + equipped trinket passives), which never change
 * during a run.
 */
export function bakeStatModifiers(
  stats: IEntityStats,
  modifiers: readonly BuffModifier[],
): void {
  const flats = new Map<keyof IEntityStats, number>();
  const percents = new Map<keyof IEntityStats, number>();
  for (const m of modifiers) {
    if (m.amount) {
      flats.set(m.stat, (flats.get(m.stat) ?? 0) + m.amount);
    }
    if (m.percent) {
      percents.set(m.stat, (percents.get(m.stat) ?? 0) + m.percent);
    }
  }
  const touched = new Set([...flats.keys(), ...percents.keys()]);
  for (const stat of touched) {
    const base = stats[stat];
    if (typeof base !== 'number') {
      continue;
    }
    const flat = flats.get(stat) ?? 0;
    const percent = percents.get(stat) ?? 0;
    (stats[stat] as number) = (base + flat) * (1 + percent / 100);
  }
}

/** Resolve a buff def at apply time: prefer the entity's run-scoped instance
 *  (carrying its trinket/socket mutations) and fall back to the registry. */
export function resolveBuffDef(
  entity: DungeonEntity,
  buffId: string,
): BuffDefinition | undefined {
  return entity.getBuffInstance(buffId) ?? BUFF_REGISTRY[buffId];
}

export function initializeBuffDefinition(defId: string): ActiveBuff | null {
  const def = BUFF_REGISTRY[defId];
  if (!def) {
    return null;
  }
  return {
    def,
    remainingTicks:
      def.durationMs !== undefined ? msToTicks(def.durationMs) : undefined,
  };
}

export function tickBuffs(entity: DungeonEntity, deltaTime: number): void {
  const buffs = entity.getActiveBuffs();
  for (let i = buffs.length - 1; i >= 0; i--) {
    const buff = buffs[i];

    // DOT tick before the duration decrement so a buff applied on frame N
    // deals its first tick at the right moment, not the last.
    if (buff.def.dot) {
      buff.dotElapsedTicks = (buff.dotElapsedTicks ?? 0) + deltaTime;
      const intervalTicks = msToTicks(buff.def.dot.intervalMs);
      while (buff.dotElapsedTicks >= intervalTicks) {
        buff.dotElapsedTicks -= intervalTicks;
        const stacks = buff.stacks ?? 1;
        const amount = buff.def.dot.amount * stacks;
        entity.takeDamage(amount, false, buff.def.dot.element);
      }
    }

    if (buff.def.stackGainIntervalMs !== undefined) {
      const cap = buff.def.maxStacks ?? 1;
      if ((buff.stacks ?? 1) < cap) {
        buff.stackGainElapsed = (buff.stackGainElapsed ?? 0) + deltaTime;
        const intervalTicks = msToTicks(buff.def.stackGainIntervalMs);
        while (
          buff.stackGainElapsed >= intervalTicks &&
          (buff.stacks ?? 1) < cap
        ) {
          buff.stackGainElapsed -= intervalTicks;
          buff.stacks = (buff.stacks ?? 1) + 1;
        }
      }
    }

    if (buff.remainingTicks !== undefined) {
      buff.remainingTicks -= deltaTime;
      if (buff.remainingTicks <= 0) {
        entity.removeBuff(buff);
      }
    }
  }

  const stats = entity.stats;
  if (stats.shieldRegen > 0 && entity.shield < stats.maxShield) {
    entity.gainShield((stats.shieldRegen * deltaTime) / 1000);
  }

  const OOC_THRESHOLD_MS = 3000;
  if (
    stats.lifeRegenPerSecond > 0 &&
    entity.life < stats.maxLife &&
    Date.now() - entity.lastDamageTakenAt > OOC_THRESHOLD_MS
  ) {
    entity.gainHealth((stats.lifeRegenPerSecond * deltaTime) / 1000);
  }
}

function buildBuffContext(
  entity: DungeonEntity,
  extras?: EventPayload,
): EffectContext {
  // Seed affectedCells with the event position so hooks using cell-based
  // handlers (applyBuffInNeighborsCells, etc.) have a non-empty iteration
  // root — e.g. Saigneur's onKill bleed needs the victim's cell.
  const affectedCells =
    extras?.eventPosX !== undefined && extras?.eventPosY !== undefined
      ? [{ x: extras.eventPosX, y: extras.eventPosY }]
      : [];
  return {
    caster: entity,
    direction: entity.direction as DirectionEnum,
    affectedCells,
    killSourceSpellId: extras?.killSourceSpellId,
    eventPosX: extras?.eventPosX,
    eventPosY: extras?.eventPosY,
  };
}

/**
 * Optional payload passed by `emitEntityEvent` to hooks. Lets modifier
 * trackers (cf. Mort-Cyclique, Festin) compare the carrier buff's
 * `linkedSpellId` with the spell that originated the event.
 */
export interface EventPayload {
  killSourceSpellId?: string;
  eventPosX?: number;
  eventPosY?: number;
}

/**
 * `onDeath` short-circuit: if at any point a handler restores HP
 * (`!entity.isDead()`), iteration stops. Prevents a second revive
 * buff/item from needlessly consuming its resources when death is
 * already cancelled.
 */
export function emitEntityEvent(
  entity: DungeonEntity,
  event: EntityEvent,
  payload?: EventPayload,
): void {
  if (event === 'onFloorEnter') {
    const allBuffs = entity.getActiveBuffs();
    for (let i = allBuffs.length - 1; i >= 0; i--) {
      if (allBuffs[i].def.untilFloorEnd) {
        entity.removeBuff(allBuffs[i]);
      }
    }
  }
  const buffs = entity.getActiveBuffs();
  for (const buff of [...buffs]) {
    if (!buff.def.hooks) {
      continue;
    }
    for (const hook of buff.def.hooks) {
      if (hook.event !== event) {
        continue;
      }
      if (!checkEffectConditions(entity, hook.conditions)) {
        continue;
      }
      const handler = EFFECT_HANDLERS[hook.handler];
      if (handler) {
        const ctx = buildBuffContext(entity, payload);
        handler(ctx, { ...hook.params, _buff: buff });
      }
      if (event === 'onDeath' && !entity.isDead()) {
        return;
      }
    }
  }
  if (entity.type === DungeonEntityType.PLAYER) {
    emitItemListeners(entity as PlayerEntity, event);
  }
}
